import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTable, useSortBy, useFilters, useGlobalFilter, usePagination } from 'react-table'
import { useParams } from "react-router-dom";
import axios from "axios";
import styled from 'styled-components'

import Pagination from "./Pagination.js";
import StyledInput from "./StyledInput.js"

// Define a default UI for filtering
function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter },
}) {
    // const count = preFilteredRows.length
    const { t } = useTranslation();

    return (
        <StyledInput
            value={filterValue || ''}
            onChange={e => {
                setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
            }}
            // placeholder={`${count} records...`}
            placeholder={t('texts.list.search')}
        />
    )
}

// const initialState = { pageIndex: 0 };

function Table({ data }) {

    const { lang } = useParams();
    const { t, i18n } = useTranslation();

    // Change the language dynamically
    React.useEffect(() => {
        i18n.changeLanguage(lang);
    }, [lang, i18n]);

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
            // width: "auto",
        }),
        []
    )

    const columns = React.useMemo(() => [
        {
            Header: 'Id',
            accessor: 'id',
            show: false,
        },
        {
            Header: t('texts.list.kategorie'),
            accessor: 'Kategorie',
            width: 100,
        },
        {
            Header: () => <span dangerouslySetInnerHTML={{__html: t('texts.list.no1')}} />,
            accessor: 'No1',
            width: 100,
        },
    ], [t]);

    const initialState = React.useMemo(() => ({ pageIndex: 0 }), []);
    
    const getTopLevelZeroCount = (rows) => { return rows.filter(row => row.original.TopLevel === 0).length; };

    var table = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            // filterTypes,
            initialState,
        },
        useFilters, // useFilters!
        useGlobalFilter, // useGlobalFilter!
        useSortBy,
        usePagination,
        // useBlockLayout
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page, // Instead of using 'rows', we'll use page,
        prepareRow,
        preGlobalFilteredRows
    } = table;

    return (
        <>
            <table {...getTableProps({ style: { width: "100%" } })} id="categorylist_table">
                <thead>
                    {headerGroups.map(headerGroup => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                        return (
                            <tr key={key} {...headerGroupProps}>
                                {headerGroup.headers.map(column => {
                                    const { key, ...headerProps } = column.getHeaderProps();
                                    return (
                                        <th key={key} {...headerProps} className={'table-header-' + column.id}>
                                            <span className="title" {...column.getSortByToggleProps()}>{column.render('Header')}</span>
                                            {/* Add a sort direction indicator */}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? ' 🔽'
                                                        : ' 🔼'
                                                    : ''}
                                            </span>
                                            {/* Render the columns filter UI */}
                                            <div>{column.canFilter ? column.render('Filter') : null}</div>
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    }
                    )}
                    <Pagination table={table} t={t} showTour={false} selectionText='texts.list.selection_category' getRowCount={getTopLevelZeroCount}/>
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row)
                        const { key, ...rowProps } = row.getRowProps();
                        return (
                            <tr key={key} {...rowProps}>
                                {row.cells.map(cell => {
                                    const { key, ...cellProps } = cell.getCellProps();
                                    return <td key={key} {...cellProps}
                                        style={{
                                            backgroundColor: row.original.TopLevel ? 'yellow' : 'inherit',
                                        }}
                                    >{cell.render('Cell')}</td>
                                })}
                            </tr>
                        )
                    })}
                </tbody>
                <tfoot>
                    {preGlobalFilteredRows.length > 0 && <Pagination table={table} t={t} showTour={false} selectionText='texts.list.selection_category' getRowCount={getTopLevelZeroCount}/>}
                </tfoot>
            </table >
        </>
    )
}

const Styles = styled.div`
  padding: 1rem;

  table {
    border-spacing: 0;
    border: 1px solid black;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;

      :last-child {
        border-right: 0;
      }
    }

    th {
      vertical-align: bottom;
    }

    th span {
      background-color: transparent;
      color: white;
    }

    th span.title {
      text-transform: uppercase;
      margin-bottom: 1rem;
      display: inline-block;
    }

    thead > tr:nth-child(1) > th {
      background-color: var(--fire);
    }

    thead > tr:nth-child(2) > th,
    tfoot > tr > th {
      background-color: black;
    }

    th > div,
    th > div > div {
      background-color: transparent
    }
  }
`

const CategoryList = () => {
    const [data, setData] = useState([]);

    const { lang } = useParams();
    const { t, i18n } = useTranslation();
    // Change the language dynamically
    React.useEffect(() => {
        i18n.changeLanguage(lang);

        var url = `${process.env.REACT_APP_DATA_HOST}/${lang}/category_list`
        axios.get(url).then(res => {
            const data = res.data.data;
            // console.log(data);
            setData(data);
        })
    }, [lang, i18n]);

    return (
        <>
            <h2>{t('texts.categorylist.title')}</h2>
            <div>
                {t('texts.categorylist.text1')}
            </div>
            <Styles>
                <Table data={data} />
            </Styles>
        </>
    )
}

export default CategoryList;